import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import StreamGifts, { Gift } from './StreamGifts';
import { getImageUrl } from '../services/api';
import { Row } from './Layout';
import { socketService } from '../services/socket';
import { webRTCService } from '../services/webrtc';

// Types (reused from SimpleStreamsPage)
interface Moderator {
    id: string;
    username: string;
    avatar: string;
    isOnline: boolean;
    isOwner: boolean;
    country?: string;
}

interface ActiveStreamViewProps {
    streamTitle: string;
    onStopStream: () => void;
    videoRef: React.RefObject<HTMLVideoElement>;
    moderators: Moderator[];
    onAddModerator: () => void;
    onRemoveModerator: (id: string) => void;
    newModeratorName: string;
    setNewModeratorName: (name: string) => void;
    user: any; // Ideally User type
    mediaStream?: MediaStream | null;
    onFlipCamera?: () => void;
    onViewProfile?: (userId: string) => void;
}

interface Heart {
    left: number;
    animationDuration: number;
}

interface Heart {
    id: number;
    left: number;
    animationDuration: number;
}

interface VideoEffect {
    id: string;
    name: string;
    filter: string;
    overlay?: string; // CSS background property
    blendMode?: string; // CSS mix-blend-mode
    opacity?: number;
    previewColor: string;
}

const VIDEO_EFFECTS: VideoEffect[] = [
    { id: 'none', name: 'Normal', filter: 'none', previewColor: '#ddd' },
    {
        id: 'beauty',
        name: 'Güzellik',
        filter: 'brightness(105%) contrast(90%) saturate(110%) blur(0.5px)',
        previewColor: '#ffe0e0'
    },
    {
        id: 'cinema',
        name: 'Sinema',
        filter: 'contrast(110%) saturate(110%) brightness(95%) sepia(20%)',
        overlay: 'linear-gradient(to bottom, rgba(0,0,0,0.2), transparent, rgba(0,0,0,0.2))',
        blendMode: 'overlay',
        previewColor: '#2c3e50'
    },
    {
        id: 'vintage',
        name: 'Vintage',
        filter: 'sepia(50%) contrast(95%) saturate(90%)',
        overlay: 'radial-gradient(circle, rgba(255,255,255,0.1), rgba(165, 42, 42, 0.4))',
        blendMode: 'multiply',
        previewColor: '#dbf6e5'
    },
    {
        id: 'noir',
        name: 'Noir',
        filter: 'grayscale(100%) contrast(120%) brightness(90%)',
        overlay: 'radial-gradient(circle, transparent 60%, black 100%)', // Vignette
        blendMode: 'multiply',
        previewColor: '#333'
    },
    {
        id: 'sunset',
        name: 'Gün Batımı',
        filter: 'contrast(110%) saturate(130%)',
        overlay: 'linear-gradient(45deg, rgba(255, 0, 128, 0.2), rgba(255, 165, 0, 0.2))',
        blendMode: 'screen',
        previewColor: '#ff7e5f'
    },
    {
        id: 'cool',
        name: 'Buz Mavisi',
        filter: 'hue-rotate(180deg) saturate(80%) brightness(110%)',
        overlay: 'rgba(0, 255, 255, 0.1)',
        blendMode: 'overlay',
        previewColor: '#00bcd4'
    },
    {
        id: 'drama',
        name: 'Drama',
        filter: 'contrast(140%) saturate(0%) brightness(90%)',
        previewColor: '#555'
    },
];

const ActiveStreamView: React.FC<ActiveStreamViewProps> = ({
    streamTitle,
    onStopStream,
    videoRef,
    moderators,
    onAddModerator,
    onRemoveModerator,
    newModeratorName,
    setNewModeratorName,
    user,
    mediaStream,
    onFlipCamera,
    onViewProfile
}) => {
    const { t } = useTranslation();
    const [showModerationPanel, setShowModerationPanel] = useState(false);
    const [showEffectsPanel, setShowEffectsPanel] = useState(false);

    // Stream Timer Logic
    const [streamDuration, setStreamDuration] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setStreamDuration(prev => prev + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const formatDuration = (seconds: number) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    // Store the full effect object, not just the filter string
    const [currentEffect, setCurrentEffect] = useState<VideoEffect>(VIDEO_EFFECTS[0]);

    const [localModerators, setLocalModerators] = useState<Moderator[]>([]);
    const [localModName, setLocalModName] = useState('');

    const handleLocalAddMod = () => {
        if (!localModName.trim()) return;
        setLocalModerators(prev => [...prev, {
            id: Date.now().toString(),
            username: localModName.trim(),
            avatar: '👤',
            isOnline: true,
            isOwner: false
        }]);
        setLocalModName('');
    };

    const handleLocalRemoveMod = (id: string) => {
        setLocalModerators(prev => prev.filter(m => m.id !== id));
    };

    const [messages, setMessages] = useState<{ id: string, user: string, userId?: string, text: string, isGift?: boolean, gift?: Gift, avatar?: string }[]>([]);
    const [guestRequests, setGuestRequests] = useState<any[]>([]);
    const [guests, setGuests] = useState<{ id: string, stream: MediaStream }[]>([]);
    const streamId = user?.streamKey || 'temp-stream-key';
    const [newMessage, setNewMessage] = useState('');
    const [likes, setLikes] = useState(0);
    const [hearts, setHearts] = useState<Heart[]>([]);
    const [showGiftPanel, setShowGiftPanel] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const [muted, setMuted] = useState(false);
    const [camOff, setCamOff] = useState(false);
    const [viewerCount, setViewerCount] = useState(0);

    // Auto-scroll chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (videoRef.current) {
            if (mediaStream) {
                videoRef.current.srcObject = mediaStream;
                mediaStream.getAudioTracks().forEach(track => { track.enabled = !muted; });
                mediaStream.getVideoTracks().forEach(track => { track.enabled = !camOff; });
            }
            // Apply CSS filter
            videoRef.current.style.filter = currentEffect.filter;
        }
    }, [mediaStream, videoRef, currentEffect, muted, camOff]);

    // Socket connection for chat and guest requests
    useEffect(() => {
        const socket = socketService.getSocket();
        if (socket) {
            if (!socket.connected) socket.connect();
            
            socket.emit('join-stream-room', streamId);
            
            socket.on('streamMessage', (data: any) => {
                setMessages(prev => [...prev.slice(-15), {
                    id: Date.now().toString(),
                    user: data.user?.username || 'Kullanıcı',
                    userId: data.user?._id || data.user?.id,
                    text: data.text,
                    avatar: data.user?.profileImage,
                    isGift: data.isGift,
                    gift: data.gift
                }]);
            });

            socket.on('guestRequest', (guestUser: any) => {
                setGuestRequests(prev => {
                    if (prev.find(u => u._id === guestUser._id)) return prev;
                    return [...prev, guestUser];
                });
            });

            socket.on('viewerCountUpdate', (count: number) => {
                setViewerCount(count);
            });
        }
        
        webRTCService.onRemoteStream = (socketId, stream, incomingUserId) => {
            // New guest stream arrived!
            setGuests(prev => {
                if (prev.find(g => g.id === incomingUserId)) return prev;
                return [...prev, { id: incomingUserId, stream }];
            });
        };

        return () => {
            if (socket) {
                socket.off('streamMessage');
                socket.off('guestRequest');
                socket.off('viewerCountUpdate');
                socket.emit('leave-stream-room', streamId);
            }
            webRTCService.onRemoteStream = undefined;
        };
    }, [streamId]);

    const handleSendMessage = () => {
        if (!newMessage.trim() || !user) return;
        const socket = socketService.getSocket();
        if (socket) {
            socket.emit('streamMessage', {
                streamId,
                text: newMessage,
                user: { _id: user.id || (user as any)._id, username: user.username, profileImage: user.profileImage }
            });
            setNewMessage('');
        }
    };
    
    const acceptGuest = (guestUser: any) => {
        const socket = socketService.getSocket();
        if (socket) {
            socket.emit('acceptGuest', { streamId, guestId: guestUser._id });
            setGuestRequests(prev => prev.filter(u => u._id !== guestUser._id));
        }
    };

    const rejectGuest = (guestUser: any) => {
        const socket = socketService.getSocket();
        if (socket) {
            socket.emit('rejectGuest', { guestId: guestUser._id });
            setGuestRequests(prev => prev.filter(u => u._id !== guestUser._id));
        }
    };

    const removeGuest = (guestId: string) => {
        const socket = socketService.getSocket();
        if (socket) {
            socket.emit('removeGuest', { guestId });
            setGuests(prev => prev.filter(g => g.id !== guestId));
        }
    };

    const handleLike = () => {
        setLikes(prev => prev + 1);
        const newHeart = {
            id: Date.now(),
            left: Math.random() * 40 + 30, // Random position between 30% and 70%
            animationDuration: Math.random() * 2 + 3 // Random duration 3-5s
        };
        setHearts(prev => [...prev, newHeart]);

        // Remove heart after animation
        setTimeout(() => {
            setHearts(prev => prev.filter(h => h.id !== newHeart.id));
        }, 4000);
    };

    // Inject styles for animations
    const handleSendGift = (gift: Gift) => {
        setMessages(prev => [...prev.slice(-15), {
            id: Date.now().toString(),
            user: user?.username || 'Ben',
            text: `${gift.icon} ${gift.name} gönderdi!`,
            isGift: true,
            gift: gift,
            avatar: user?.profileImage
        }]);
        setShowGiftPanel(false);
        // Trigger generic like animation for gift too
        for (let i = 0; i < 5; i++) setTimeout(handleLike, i * 200);
    };

    // Guest Slots Logic
    interface GuestSlot {
        id: number;
        status: 'empty' | 'locked' | 'live';
        user?: {
            name: string;
            avatar: string;
        };
    }

    // Mock functionality removed
    useEffect(() => {
        const styleSheet = document.createElement('style');
        styleSheet.textContent = `
            @keyframes floatUp {
                0% { transform: translateY(0) scale(1); opacity: 1; }
                100% { transform: translateY(-300px) scale(1.5); opacity: 0; }
            }
            @keyframes slideUp {
                from { transform: translateY(100%); }
                to { transform: translateY(0); }
            }
            .heart-anim {
                animation: floatUp 4s ease-out forwards;
            }
            .slide-up {
                animation: slideUp 0.3s ease-out forwards;
            }
            .hide-scrollbar::-webkit-scrollbar {
                display: none;
            }
        `;
        document.head.appendChild(styleSheet);
        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);

    // Bind media streams to the video element
    useEffect(() => {
        if (videoRef.current && mediaStream) {
            videoRef.current.srcObject = mediaStream;
        }
    }, [mediaStream, videoRef]);

    return (
        <div style={styles.container}>
            {/* Video Background */}
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={styles.videoBackground}
            />

            {/* Guest PiP Videos */}
            <div style={{ position: 'absolute', top: 120, right: 16, display: 'flex', flexDirection: 'column', gap: 10, zIndex: 15 }}>
                {guests.map((guest, idx) => (
                    <div key={guest.id} style={{ width: 120, height: 160, borderRadius: 12, overflow: 'hidden', background: '#000', border: '2px solid #FF2A85', position: 'relative' }}>
                        <button 
                            onClick={() => removeGuest(guest.id)}
                            style={{ position: 'absolute', top: 5, right: 5, background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: 24, height: 24, zIndex: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            ✕
                        </button>
                        <div 
                            style={{ width: '100%', height: '100%' }}
                            ref={(el) => {
                                if (el) {
                                    if ((guest.stream as any).agoraVideoTrack) {
                                        if (el.dataset.guestId !== guest.id) {
                                            el.innerHTML = '';
                                            (guest.stream as any).agoraVideoTrack.play(el);
                                            el.dataset.guestId = guest.id;
                                        }
                                    } else {
                                        let video = el.querySelector('video');
                                        if (!video) {
                                            video = document.createElement('video');
                                            video.style.width = '100%';
                                            video.style.height = '100%';
                                            video.style.objectFit = 'cover';
                                            video.autoplay = true;
                                            video.playsInline = true;
                                            video.muted = true;
                                            el.appendChild(video);
                                        }
                                        if (video.srcObject !== guest.stream) {
                                            video.srcObject = guest.stream;
                                            video.play().catch(e => console.error('Guest PIP play error:', e));
                                        }
                                    }
                                }
                            }}
                        />
                    </div>
                ))}
            </div>

            {/* Guest Requests Popup */}
            {guestRequests.length > 0 && (
                <div style={{
                    position: 'absolute', top: 80, left: '50%', transform: 'translateX(-50%)',
                    background: 'rgba(0,0,0,0.8)', padding: 12, borderRadius: 12, zIndex: 20,
                    backdropFilter: 'blur(10px)', border: '1px solid #FF2A85', display: 'flex', flexDirection: 'column', gap: 8
                }}>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>Misafir İstekleri</div>
                    {guestRequests.map(req => (
                        <div key={req._id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ color: '#fff', fontSize: 14 }}>{req.username}</span>
                            <button onClick={() => acceptGuest(req)} style={{ background: '#22C55E', border: 'none', borderRadius: 4, padding: '4px 8px', color: '#fff' }}>Kabul</button>
                            <button onClick={() => rejectGuest(req)} style={{ background: '#FF5C5C', border: 'none', borderRadius: 4, padding: '4px 8px', color: '#fff' }}>Red</button>
                        </div>
                    ))}
                </div>
            )}

            {/* Effect Overlay */}
            {currentEffect.overlay && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: currentEffect.overlay,
                        mixBlendMode: (currentEffect.blendMode as any) || 'normal',
                        opacity: currentEffect.opacity || 0.6,
                        pointerEvents: 'none',
                        zIndex: 1, // Above video, below UI
                    }}
                />
            )}

            {/* Gradient Overlay */}
            <div style={styles.gradientOverlay} />

            {/* UI Layer */}
            <div style={styles.uiLayer}>

                {/* Top Bar */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '50px 14px 13px', zIndex: 20, pointerEvents: 'auto', background: 'linear-gradient(to bottom,rgba(0,0,0,.85),transparent)' }}>
                    <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Row style={{ gap: 7, alignItems: 'center' }}>
                            <span className="live-badge" style={{ background: 'linear-gradient(135deg,#FF3B3B,#FF6B6B)', color: '#fff', fontFamily: 'Syne', fontWeight: 800, fontSize: 10, padding: '3px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <span className="dot-live" style={{ width: 6, height: 6, background: '#fff', borderRadius: '50%', flexShrink: 0 }} />CANLI
                            </span>
                            <div style={{ background: 'rgba(0,0,0,.5)', borderRadius: 9, padding: '4px 11px', display: 'flex', gap: 4, alignItems: 'center' }}>
                                <span style={{ fontSize: 10 }}>👁️</span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cyan)' }}>{viewerCount}</span>
                            </div>
                            <div style={{ background: 'rgba(0,0,0,.5)', borderRadius: 9, padding: '4px 11px', color: 'rgba(255,255,255,.65)', fontSize: 12 }}>
                                {formatDuration(streamDuration)}
                            </div>
                        </Row>
                        <button onClick={onStopStream} style={{ background: 'rgba(255,60,60,.2)', border: '1px solid rgba(255,60,60,.4)', borderRadius: 11, padding: '7px 13px', color: '#FF4444', fontSize: 12, cursor: 'pointer', fontFamily: 'Syne', fontWeight: 700, pointerEvents: 'auto' }}>Bitir</button>
                    </Row>
                </div>

                {/* Guest Slots Container Removed because we use the real ones above */}
                {guests.length < 3 && (
                    <div style={{ position: 'absolute', right: 16, top: 120 + (guests.length * 170), display: 'flex', flexDirection: 'column', gap: 10, zIndex: 14 }}>
                        {[...Array(3 - guests.length)].map((_, i) => (
                            <div key={`empty-${i}`} style={{ width: 120, height: 160, borderRadius: 12, border: '2px dashed rgba(255,42,133,0.5)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(10px)' }}>
                                <div style={{ color: 'rgba(255,42,133,0.8)', fontSize: 32, marginBottom: 4, fontWeight: 300 }}>+</div>
                                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600 }}>Misafir</div>
                            </div>
                        ))}
                    </div>
                )}

            </div>


            {/* Floating Hearts Container */}
            <div style={styles.heartsContainer}>
                {hearts.map(heart => (
                    <div
                        key={heart.id}
                        className="heart-anim"
                        style={{
                            ...styles.floatingHeart,
                            left: `${heart.left}%`,
                            animationDuration: `${heart.animationDuration}s`
                        }}
                    >
                        {['❤️', '🔥', '🥰', '😍'][Math.floor(Math.random() * 4)]}
                    </div>
                ))}
            </div>

            {/* Bottom Area */}
            <div style={styles.bottomArea}>

                {/* Chat Messages */}
                <div style={styles.chatContainer} className="hide-scrollbar">
                    {messages.map(msg => (
                        <div key={msg.id} style={{...styles.chatMessage, cursor: 'pointer'}} onClick={() => msg.userId && onViewProfile && onViewProfile(msg.userId)}>
                            {msg.isGift ? (
                                <div style={styles.giftMessage}>
                                    <img src={msg.avatar ? getImageUrl(msg.avatar) : 'https://via.placeholder.com/30'} style={styles.msgAvatar} alt="user" />
                                    <div style={styles.giftContent}>
                                        <span style={styles.giftUser}>{msg.user}</span>
                                        <span style={styles.giftText}>
                                            {msg.gift?.icon} {msg.gift?.name} gönderdi!
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <img src={msg.avatar ? getImageUrl(msg.avatar) : 'https://via.placeholder.com/30'} style={styles.msgAvatar} alt="user" />
                                    <div style={styles.msgContent}>
                                        <span style={styles.msgUser}>{msg.user}</span>
                                        <span style={styles.msgText}>{msg.text}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                    <div ref={chatEndRef} />
                </div>

                {/* Bottom Controls */}
                <div style={{ padding: '0 20px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ flex: 1, background: 'rgba(0,0,0,.4)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 24, display: 'flex', alignItems: 'center', padding: '0 16px', height: 48 }}>
                            <input
                                id="chat-input"
                                type="text"
                                placeholder="Yorum ekle..."
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                style={{ flex: 1, background: 'none', border: 'none', color: '#fff', fontSize: 14, outline: 'none' }}
                            />
                        </div>
                        <button style={{ width: 48, height: 48, borderRadius: 24, background: 'linear-gradient(135deg,var(--cyan),var(--blue))', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, boxShadow: '0 4px 15px rgba(0,229,204,.3)', cursor: 'pointer' }} onClick={handleSendMessage}>➤</button>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 13, marginTop: 10 }}>
                        {[
                            { icon: muted ? '🔇' : '🎤', label: muted ? 'Aç' : 'Kapat', action: () => setMuted(m => !m), active: muted, color: '#FF5C5C' },
                            { icon: camOff ? '📵' : '📷', label: camOff ? 'Aç' : 'Kapat', action: () => setCamOff(c => !c), active: camOff, color: '#FF5C5C' },
                            { icon: '🔄', label: 'Çevir', action: onFlipCamera || (() => { }), active: false, color: '#4CAF50' },
                            { icon: '✨', label: 'Efekt', action: () => setShowEffectsPanel(true), active: false, color: 'var(--purple)' },
                            { icon: '🛡️', label: 'Mod', action: () => setShowModerationPanel(true), active: false, color: '#3B82F6' }
                        ].map((btn, i) => (
                            <button key={i} onClick={btn.action} style={{
                                background: btn.active ? `${btn.color}33` : 'rgba(255,255,255,.08)',
                                border: `1px solid ${btn.active ? `${btn.color}55` : 'rgba(255,255,255,.1)'}`,
                                borderRadius: 14, width: 52, height: 52, cursor: 'pointer',
                                display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', justifyContent: 'center', transition: 'all .2s'
                            }}>
                                <span style={{ fontSize: 20 }}>{btn.icon}</span>
                                <span style={{ fontSize: 8, color: btn.active ? btn.color : 'rgba(255,255,255,.5)', fontFamily: 'DM Sans', fontWeight: 600 }}>{btn.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Moderation Panel (Bottom Sheet) */}
            {
                showModerationPanel && (
                    <div style={styles.modalOverlay} onClick={() => setShowModerationPanel(false)}>
                        <div style={styles.bottomSheet} className="slide-up" onClick={e => e.stopPropagation()}>
                            <div style={styles.sheetHeader}>
                                <h3>🛡️ Moderasyon</h3>
                                <button onClick={() => setShowModerationPanel(false)} style={styles.closeSheetBtn}>✕</button>
                            </div>

                            <div style={styles.addModContainer}>
                                <input
                                    style={styles.modInput}
                                    placeholder="Kullanıcı adı..."
                                    value={localModName}
                                    onChange={(e) => setLocalModName(e.target.value)}
                                />
                                <button style={styles.addModBtn} onClick={handleLocalAddMod}>Ekle</button>
                            </div>

                            <div style={styles.modList}>
                                {localModerators.map(mod => (
                                    <div key={mod.id} style={styles.modItem}>
                                        <div style={styles.modInfo}>
                                            <span style={styles.modAvatar}>{mod.avatar}</span>
                                            <span>{mod.username}</span>
                                            {mod.isOwner && <span style={styles.ownerBadge}>👑</span>}
                                        </div>
                                        {!mod.isOwner && (
                                            <button
                                                onClick={() => handleLocalRemoveMod(mod.id)}
                                                style={styles.removeModBtn}
                                            >
                                                Kaldır
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Effects Panel */}
            {
                showEffectsPanel && (
                    <div style={styles.modalOverlay} onClick={() => setShowEffectsPanel(false)}>
                        <div style={styles.bottomSheet} className="slide-up" onClick={e => e.stopPropagation()}>
                            <div style={styles.sheetHeader}>
                                <h3>✨ Efektler</h3>
                                <button onClick={() => setShowEffectsPanel(false)} style={styles.closeSheetBtn}>✕</button>
                            </div>
                            <div style={styles.effectsGrid}>
                                {VIDEO_EFFECTS.map(effect => (
                                    <button
                                        key={effect.id}
                                        onClick={() => setCurrentEffect(effect)}
                                        style={{
                                            ...styles.effectBtn,
                                            opacity: currentEffect.id === effect.id ? 1 : 0.7,
                                            transform: currentEffect.id === effect.id ? 'scale(1.1)' : 'scale(1)',
                                        }}
                                    >
                                        <div style={{
                                            ...styles.effectPreview,
                                            background: effect.previewColor,
                                            filter: effect.filter === 'none' ? 'none' : effect.filter,
                                            border: currentEffect.id === effect.id ? '2px solid #ff007f' : '2px solid transparent',
                                            boxShadow: currentEffect.id === effect.id ? '0 0 12px rgba(255, 0, 127, 0.6)' : '0 2px 5px rgba(0,0,0,0.2)',
                                        }}>
                                            {/* Optional: Add mini overlay to preview */}
                                            {effect.overlay && (
                                                <div style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    background: effect.overlay,
                                                    mixBlendMode: (effect.blendMode as any),
                                                    opacity: 0.8
                                                }} />
                                            )}
                                        </div>
                                        <span>{effect.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Gift Panel */}
            <StreamGifts
                isOpen={showGiftPanel}
                onClose={() => setShowGiftPanel(false)}
                onSendGift={handleSendGift}
                balance={user?.coins || 100}
            />
        </div >
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        position: 'absolute', // Changed from fixed to absolute to fit within the App simulator container
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        zIndex: 9999,
        overflow: 'hidden',
        overscrollBehavior: 'none',
        touchAction: 'none',
    },
    videoBackground: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transform: 'scaleX(-1)',
    },
    gradientOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 20%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.9) 100%)',
        pointerEvents: 'none',
    },
    uiLayer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '16px',
        paddingTop: '70px', // Adjusted for better fit
        paddingBottom: '20px',
        boxSizing: 'border-box',
        pointerEvents: 'none', // Allow clicks to pass through transparent areas
    },
    topBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10,
        pointerEvents: 'auto', // Re-enable clicks
    },
    streamerProfile: {
        display: 'flex',
        alignItems: 'center',
        background: 'rgba(20, 20, 24, 0.6)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: '28px',
        padding: '4px',
        paddingRight: '12px',
        gap: '8px',
        border: '1px solid rgba(255,255,255,0.05)',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    },
    avatarContainer: {
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        overflow: 'hidden',
        border: '1px solid #ff007f',
        padding: '1px',
        background: '#fff',
    },
    avatar: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: '50%',
    },
    streamerInfo: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    username: {
        color: '#fff',
        fontWeight: 'bold',
        fontSize: '11px',
        lineHeight: '1.2',
        marginBottom: '1px',
    },
    viewers: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: '9px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: '500',
    },
    liveDot: {
        width: '6px',
        height: '6px',
        background: '#4CAF50',
        borderRadius: '50%',
        boxShadow: '0 0 6px #4CAF50',
    },
    followButton: {
        background: '#ff007f',
        color: '#fff',
        border: 'none',
        borderRadius: '14px',
        padding: '4px 10px',
        fontSize: '10px',
        fontWeight: '700',
        cursor: 'pointer',
        marginLeft: '4px',
        boxShadow: '0 2px 5px rgba(255, 0, 127, 0.4)',
        transition: 'transform 0.1s',
    },
    topRightActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    viewerBadge: {
        display: 'none', // Removed
    },
    closeButton: {
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        background: 'rgba(20, 20, 24, 0.6)',
        border: '1px solid rgba(255,255,255,0.05)',
        color: '#fff',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        transition: 'all 0.2s',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    },
    guestSlotsContainer: {
        position: 'absolute',
        top: '120px',
        right: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        zIndex: 15,
        pointerEvents: 'auto',
    },
    guestSlot: {
        width: '90px',
        height: '110px',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(8px)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer', // Clickable for demo
        transition: 'transform 0.2s',
    },
    guestVideo: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    emptySlot: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        color: 'rgba(255, 255, 255, 0.5)',
    },
    plusIcon: {
        fontSize: '24px',
        fontWeight: '300',
    },
    slotLabel: {
        fontSize: '10px',
        fontWeight: '500',
    },
    guestBadge: {
        position: 'absolute',
        bottom: '4px',
        left: '4px',
        right: '4px',
        background: 'rgba(0,0,0,0.5)',
        borderRadius: '4px',
        padding: '2px 4px',
        color: '#fff',
        fontSize: '9px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    soundWaves: {
        color: '#4CAF50',
        fontWeight: 'bold',
        fontSize: '10px',
    },
    kickButton: {
        position: 'absolute',
        top: '4px',
        right: '4px',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        background: 'rgba(244, 67, 54, 0.8)',
        border: 'none',
        color: '#fff',
        fontSize: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    rightSidebar: {
        position: 'absolute',
        right: '16px',
        bottom: '120px', // Adjusted position
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        alignItems: 'center',
        zIndex: 20,
        pointerEvents: 'auto', // Re-enable clicks
    },
    sideAction: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
    },
    actionIcon: {
        fontSize: '28px',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
    },
    actionLabel: {
        color: '#fff',
        fontSize: '10px',
        fontWeight: '600',
        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
    },
    heartsContainer: {
        position: 'absolute',
        bottom: '100px',
        right: '20px',
        width: '100px',
        height: '300px',
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 4,
    },
    floatingHeart: {
        position: 'absolute',
        bottom: '0',
        fontSize: '24px',
        opacity: 0,
    },
    bottomArea: {
        position: 'absolute',
        bottom: '0',
        left: '0',
        right: '0',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        zIndex: 10,
        pointerEvents: 'auto',
        paddingBottom: '20px',
    },
    chatContainer: {
        flex: 1,
        maxHeight: '260px',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end', // Stick to bottom
        gap: '8px',
        maskImage: 'linear-gradient(to top, black 80%, transparent 100%)',
        marginBottom: '8px',
        padding: '0 16px', // Standard padding
    },
    chatMessage: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        background: 'rgba(0,0,0,0.3)',
        padding: '7px 14px',
        borderRadius: '18px',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.08)',
        alignSelf: 'flex-start',
        maxWidth: '85%',
        marginBottom: '2px',
    },
    msgAvatar: {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.2)',
    },
    msgContent: {
        display: 'flex',
        flexDirection: 'column',
    },
    msgUser: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: '11px',
        fontWeight: 'bold',
        marginBottom: '2px',
    },
    msgText: {
        color: '#fff',
        fontSize: '13px',
        lineHeight: '1.4',
    },
    giftMessage: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: 'linear-gradient(135deg, rgba(233,30,99,0.8), rgba(121, 40, 202,0.8))',
        padding: '6px 12px',
        borderRadius: '20px',
        borderBottomLeftRadius: '4px',
        boxShadow: '0 4px 12px rgba(255, 0, 127, 0.3)',
    },
    giftContent: {
        display: 'flex',
        flexDirection: 'column',
    },
    giftUser: {
        color: '#ffeb3b',
        fontSize: '11px',
        fontWeight: 'bold',
    },
    giftText: {
        color: '#fff',
        fontSize: '12px',
        fontWeight: 'bold',
        textShadow: '0 1px 2px rgba(0,0,0,0.2)',
    },
    controlsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        padding: '0 4px', // Slight padding
    },
    inputWrapper: {
        flex: 1,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '28px',
        padding: '4px 4px 4px 16px',
        display: 'flex',
        alignItems: 'center',
        border: '1px solid rgba(255,255,255,0.08)',
        height: '48px',
        boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.2)',
    },
    chatInput: {
        flex: 1,
        background: 'transparent',
        border: 'none',
        color: '#fff',
        padding: '8px 0',
        fontSize: '14px',
        outline: 'none',
    },
    sendButton: {
        background: '#ff007f',
        border: 'none',
        color: '#fff',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        fontSize: '16px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: '8px',
        transition: 'transform 0.1s',
        boxShadow: '0 4px 12px rgba(255, 0, 127, 0.4)',
    },
    giftButton: {
        background: 'linear-gradient(135deg, #FFD700 0%, #ff8a00 100%)',
        border: 'none',
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        fontSize: '22px', // Larger emoji
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 15px rgba(255, 193, 7, 0.5)',
        transition: 'transform 0.2s',
    },
    likeFab: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255,255,255,0.05)',
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        fontSize: '22px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        color: '#fff',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    },
    modalOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'flex-end',
        zIndex: 2000,
    },
    bottomSheet: {
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.85)', // Semi-transparent
        backdropFilter: 'blur(16px)', // Blur effect
        borderTopLeftRadius: '24px',
        borderTopRightRadius: '24px',
        padding: '24px',
        maxHeight: '70vh',
        overflowY: 'auto',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.2)',
        borderTop: '1px solid rgba(255,255,255,0.5)',
    },
    sheetHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        paddingBottom: '10px',
    },
    closeSheetBtn: {
        background: '#f0f0f0',
        border: 'none',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        cursor: 'pointer',
    },
    addModContainer: {
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
    },
    modInput: {
        flex: 1,
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '12px',
        outline: 'none',
        background: '#f8f9fa',
    },
    addModBtn: {
        backgroundColor: '#ff007f',
        color: '#fff',
        border: 'none',
        padding: '0 20px',
        borderRadius: '12px',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    modList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    modItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
    },
    modInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    modAvatar: {
        fontSize: '20px',
    },
    ownerBadge: {
        fontSize: '12px',
    },
    removeModBtn: {
        backgroundColor: '#ff5252',
        color: '#fff',
        border: 'none',
        padding: '6px 12px',
        borderRadius: '8px',
        fontSize: '12px',
        cursor: 'pointer',
    },
    effectsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)', // 4 columns for better visibility
        gap: '12px',
        padding: '10px 0',
        overflowY: 'auto',
        maxHeight: '300px', // Prevent taking up too much space
    },
    effectBtn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#333',
        fontSize: '12px',
        fontWeight: 'bold',
    },
    effectPreview: {
        width: '48px', // Slightly larger
        height: '48px',
        borderRadius: '50%',
        backgroundColor: '#eee',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '9px',
        color: '#000',
        border: '3px solid #fff', // Thicker border
        boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
        transition: 'all 0.2s ease',
    },
};

export default ActiveStreamView;

